import { createContext, useContext } from 'react';
import { useReveal, revealState } from '../../hooks/useReveal.js';

/**
 * The bridge between the reveal hooks and the global motion stylesheet.
 *
 * A <Sequence> observes its own arrival and publishes it on context; the
 * pieces inside it (<SeqEyebrow>, <LineRise>, <SeqFollow>, <SpecRow>) read
 * that one arrival rather than each observing independently. That is what
 * keeps a section's timeline relative to a single moment instead of to
 * however each block happened to cross the fold.
 *
 * The default is `null`, meaning "no sequence above me" — deliberately not
 * `false`. These components hide themselves until their sequence arrives,
 * so a `false` default would leave any of them rendered outside a
 * <Sequence> hidden forever with nothing coming to reveal them.
 */
const SequenceContext = createContext(null);

/** @returns {boolean|null} arrival state, or null when not in a sequence. */
export const useSequenceShown = () => useContext(SequenceContext);

/**
 * Resolves a sequence-driven state for the motion attributes: inside a
 * sequence, follow it; outside one, be visible.
 */
export function useSequenceState() {
  const shown = useSequenceShown();
  return { inSequence: shown !== null, shown: shown ?? true };
}

/** A single block that fades up on arrival. */
export function Reveal({ as: Tag = 'div', className = '', children, ...rest }) {
  const [ref, shown] = useReveal({ mode: 'block' });
  return (
    <Tag ref={ref} className={className} data-reveal={revealState(shown)} {...rest}>
      {children}
    </Tag>
  );
}

/** A container whose arrival drives a timeline for everything inside it. */
export function Sequence({ as: Tag = 'div', className = '', children, ...rest }) {
  const [ref, shown] = useReveal({ mode: 'section' });
  return (
    <SequenceContext.Provider value={shown}>
      <Tag ref={ref} className={className} data-seq={revealState(shown)} {...rest}>
        {children}
      </Tag>
    </SequenceContext.Provider>
  );
}

/**
 * A heading split into lines that rise from behind their own edge.
 *
 * `onLoad` runs the CSS-keyframe variant instead of the arrival-driven one,
 * for headings that sit above the fold and shouldn't wait for a scroll the
 * visitor has already made.
 */
export function LineRise({ as: Tag = 'h2', lines, onLoad = false, className = '', ...rest }) {
  const { shown } = useSequenceState();
  return (
    <Tag
      className={className}
      data-line-rise={onLoad ? 'load' : revealState(shown)}
      {...rest}
    >
      {lines.map((line) => (
        <span key={line} data-line="">
          <span>{line}</span>
        </span>
      ))}
    </Tag>
  );
}

/** A trailing lede or button that arrives after the heading it follows. */
export function SeqFollow({ as: Tag = 'span', className = '', children, ...rest }) {
  const { shown } = useSequenceState();
  return (
    <Tag className={className} data-seq-follow={revealState(shown)} {...rest}>
      {children}
    </Tag>
  );
}
